import { RigidBody } from '@react-three/rapier';
import { Player } from './Player';
import { Interactable } from './Interactable';
import { useMemo } from 'react';
import { Html } from '@react-three/drei';

interface InteriorProps {
    type: string;
    onExit: () => void;
}

export const Interior = ({ type, onExit }: InteriorProps) => {
    const color = useMemo(() => {
        switch (type) {
            case 'house_about': return '#4a90e2';
            case 'house_personal_projects': return '#e24a4a';
            case 'house_apps': return '#f5a623';
            case 'house_games': return '#6a5acd';
            case 'house_simulations': return '#3cb371';
            default: return '#ffffff';
        }
    }, [type]);

    const title = useMemo(() => {
        switch (type) {
            case 'house_about': return 'About';
            case 'house_personal_projects': return 'Personal Projects';
            case 'house_apps': return 'Apps';
            case 'house_games': return 'Games';
            case 'house_simulations': return 'Simulations';
            default: return 'Interior';
        }
    }, [type]);

    return (
        <group>
            {/* Interior-specific Lights (Optional, scene has global lights, but we can add local ones for mood) */}
            <pointLight position={[0, 8, 0]} intensity={1} distance={20} decay={2} color="white" />

            <Player onMove={() => { }} initialPos={[0, 2, 0]} />

            {/* Room Container */}
            <group position={[0, 0, 0]}>
                {/* Floor */}
                <RigidBody type="fixed" colliders="cuboid">
                    <mesh receiveShadow rotation={[-Math.PI / 2, 0, 0]}>
                        <planeGeometry args={[20, 20]} />
                        <meshStandardMaterial color="#333" roughness={0.8} />
                    </mesh>
                </RigidBody>

                {/* Walls */}
                <RigidBody type="fixed" colliders="cuboid">
                    {/* Back Wall */}
                    <mesh position={[0, 5, -10]} receiveShadow>
                        <boxGeometry args={[20, 10, 1]} />
                        <meshStandardMaterial color={color} />
                    </mesh>
                    {/* Front Wall (with Door Hole) */}
                    {/* Implemented as two side panels for now to leave center open, but collision is simple cuboid in original. 
                         Let's proper walls.
                     */}
                </RigidBody>

                {/* Front Wall - Right */}
                <RigidBody type="fixed" colliders="cuboid" position={[6, 5, 10]}>
                    <mesh receiveShadow>
                        <boxGeometry args={[8, 10, 1]} />
                        <meshStandardMaterial color={color} />
                    </mesh>
                </RigidBody>
                {/* Front Wall - Left */}
                <RigidBody type="fixed" colliders="cuboid" position={[-6, 5, 10]}>
                    <mesh receiveShadow>
                        <boxGeometry args={[8, 10, 1]} />
                        <meshStandardMaterial color={color} />
                    </mesh>
                </RigidBody>
                {/* Front Wall - Top (Door Frame) */}
                <RigidBody type="fixed" colliders="cuboid" position={[0, 8, 10]}>
                    <mesh receiveShadow>
                        <boxGeometry args={[4, 4, 1]} />
                        <meshStandardMaterial color={color} />
                    </mesh>
                </RigidBody>

                <RigidBody type="fixed" colliders="cuboid" position={[-10, 5, 0]}>
                    <mesh receiveShadow>
                        <boxGeometry args={[1, 10, 20]} />
                        <meshStandardMaterial color={color} />
                    </mesh>
                </RigidBody>
                <RigidBody type="fixed" colliders="cuboid" position={[10, 5, 0]}>
                    <mesh receiveShadow>
                        <boxGeometry args={[1, 10, 20]} />
                        <meshStandardMaterial color={color} />
                    </mesh>
                </RigidBody>

                {/* Ceiling */}
                <RigidBody type="fixed" colliders="cuboid" position={[0, 10.5, 0]}>
                    <mesh receiveShadow>
                        <boxGeometry args={[22, 1, 22]} />
                        <meshStandardMaterial color="#222" />
                    </mesh>
                </RigidBody>
            </group>

            {/* Exit Door */}
            {/* Placed at the "Main World Entrance" side, so z=8 inside appropriate to walk out */}
            <Interactable position={[0, 2, 9]} name="Exit House" color="red" onInteract={onExit} />

            <Html position={[0, 5, -9]} center transform>
                <div style={{ color: 'white', fontSize: '2em', fontFamily: 'sans-serif' }}>
                    {title}
                </div>
            </Html>

            {/* Content Placeholder */}
            {type === 'house_about' && (
                <group position={[0, 0, -5]}>
                    <RigidBody position={[0, 2, 0]} colliders="cuboid">
                        <mesh castShadow>
                            <boxGeometry args={[2, 2, 2]} />
                            <meshStandardMaterial color="blue" />
                        </mesh>
                    </RigidBody>
                </group>
            )}

            {type === 'house_personal_projects' && (
                <group position={[0, 0, -5]}>
                    <RigidBody position={[-3, 2, 0]} colliders="cuboid">
                        <mesh castShadow>
                            <boxGeometry args={[2, 3, 0.5]} />
                            <meshStandardMaterial color="purple" />
                        </mesh>
                    </RigidBody>
                    <RigidBody position={[3, 2, 0]} colliders="cuboid">
                        <mesh castShadow>
                            <boxGeometry args={[2, 3, 0.5]} />
                            <meshStandardMaterial color="purple" />
                        </mesh>
                    </RigidBody>
                </group>
            )}

            {type === 'house_apps' && (
                <group position={[0, 0, -5]}>
                    <RigidBody position={[0, 2, 0]} colliders="cuboid">
                        <mesh castShadow>
                            <boxGeometry args={[4, 2.5, 0.5]} />
                            <meshStandardMaterial color="#f5a623" />
                        </mesh>
                    </RigidBody>
                </group>
            )}

            {type === 'house_games' && (
                <group position={[0, 0, -5]}>
                    <RigidBody position={[0, 2, 0]} colliders="cuboid">
                        <mesh castShadow>
                            <boxGeometry args={[3, 2, 1]} />
                            <meshStandardMaterial color="#6a5acd" />
                        </mesh>
                    </RigidBody>
                </group>
            )}

            {type === 'house_simulations' && (
                <group position={[0, 0, -5]}>
                    <RigidBody position={[0, 2, 0]} colliders="cuboid">
                        <mesh castShadow>
                            <boxGeometry args={[3.5, 2.5, 0.6]} />
                            <meshStandardMaterial color="#3cb371" />
                        </mesh>
                    </RigidBody>
                </group>
            )}

        </group>
    );
};
